/**
 * REDFOX PR3-B — 社交可观测前端类型 + 纯展示 helper（frontend-only 消费 PR3-A）。
 *
 * 红线：**0 verdict 二次判定**（SLA verdict/color 全来自 backend）、**0 raw 字段绑定**
 * （error_message/rate_limit_meta/secret/pepper 绝不进类型或渲染）、社交信号 0 进 VOC/NPS。
 * 字段严格对齐 PR3-A `social_source_status.py`（grep main b4bd4b5）。
 */

// ── 端点 ──
export const DATA_SOURCES_ENDPOINT = '/data-sources';
export const SOCIAL_RUNS_ENDPOINT = '/data-sources/redfox/social-runs';
/** UI **绝不**调此端点（REDFOX 无 crawl_runs，后端返 400 REDFOX_USE_SOCIAL_RUNS）。仅作显式禁用常量供测试断言。 */
export const REDFOX_CRAWL_RUNS_ENDPOINT_FORBIDDEN = '/data-sources/redfox/runs';

/** DataSources 页 / Tasks 社交子区取源级：显式消费者 → include_social=true（§D4，无 toggle）。 */
export const SOCIAL_LIST_PARAMS = { include_social: true } as const;

// ── 类型（独立于 crawler DataSource / DataSourceMonitor）──
export type SocialSlaVerdict = 'ok' | 'warn' | 'breach' | 'exempt';
export type SocialSlaColor = 'green' | 'amber' | 'red' | 'gray';

/** social run ledger 行 —— **只**匹配 PR3-A 17 public 字段，显式排除 error_message/rate_limit_meta/raw。 */
export interface SocialRun {
  run_id: number;
  week: string | null;
  run_status: string;
  platform: string;
  query: string;
  api_call_count: number | null;
  items_returned: number | null;
  after_dedupe: number | null;
  rows_inserted: number | null;
  rows_updated: number | null;
  rows_skipped: number | null;
  budget_max_calls: number | null;
  budget_max_posts: number | null;
  host_role: string | null;
  raw_field_version: string | null;
  crawl_time: string | null;
  run_reason: string | null; // normalized SOCIAL_* | null（无 raw error）
}

/** REDFOX 源级（/data-sources?include_social=true 中 source_kind='social_api' 行）。 */
export interface SocialDataSource {
  source_id: 'redfox';
  source_kind: 'social_api';
  source_type: string; // '社交'
  source_name: string;
  config_status: string; // NORMAL / PAUSED / ...
  host_role: string | null;
  budget_max_calls: number | null;
  budget_max_posts: number | null;
  latest_run: SocialRun | null;
  sla_verdict: SocialSlaVerdict;
  sla_reasons: string[]; // normalized SOCIAL_* only
  sla_color: SocialSlaColor;
  sentiment_status: string; // nlp_unverified
  disclosure: string;
}

/** crawl 行（include_social 响应里 source_kind='crawl' 的既有行；仅声明判别 + 消费字段）。 */
export interface CrawlDataSourceRow {
  source_id: string;
  source_kind: 'crawl';
  source_name: string;
  source_type: string;
  display_status: string;
  data_quality_score: number | null;
  owner: string | null;
  crawl_method: string;
  anti_crawl_risk: string;
  base_url: string;
  status: string;
  last_success_time: string | null;
}

export type DataSourceRow = CrawlDataSourceRow | SocialDataSource;

export function isSocialRow(r: DataSourceRow): r is SocialDataSource {
  return r.source_kind === 'social_api';
}

/** §D4：crawl vs social 视觉分区——按 source_kind 判别拆分。 */
export function partitionDataSources(rows: DataSourceRow[]): {
  crawl: CrawlDataSourceRow[];
  social: SocialDataSource[];
} {
  const crawl: CrawlDataSourceRow[] = [];
  const social: SocialDataSource[] = [];
  for (const r of rows) {
    if (isSocialRow(r)) social.push(r);
    else crawl.push(r);
  }
  return { crawl, social };
}

// ── 色映射（§D5：sla_color → AntD Tag color，纯查表，amber→orange，0 verdict 二次判定）──
export const SOCIAL_SLA_TAG_COLOR: Record<SocialSlaColor, string> = {
  green: 'green',
  amber: 'orange', // AntD 无 amber，展示映射为 orange（**不改 verdict 语义**）
  red: 'red',
  gray: 'default',
};

export function socialSlaTagColor(c: SocialSlaColor): string {
  return SOCIAL_SLA_TAG_COLOR[c] ?? 'default';
}

// ── normalized SOCIAL_* → 展示 label（display-only，不改 verdict / 不重算 SLA）──
export const SOCIAL_REASON_LABEL: Record<string, string> = {
  SOCIAL_OK: '正常',
  SOCIAL_NO_RUN: '暂无采集',
  SOCIAL_RUN_FAILED: '采集失败',
  SOCIAL_AUTH_FAILED: '鉴权失败',
  SOCIAL_RATE_LIMIT: '限流',
  SOCIAL_QUOTA_EXCEEDED: '配额超限',
  SOCIAL_BUDGET_EXCEEDED: '预算超限',
  SOCIAL_ZERO_DEDUPE: '去重后 0 条',
  SOCIAL_PARTIAL_WITH_DATA: '部分成功',
  SOCIAL_METADATA_MISSING: '元数据缺失',
  SOCIAL_EXEMPT: '豁免',
};

export function socialReasonLabel(code: string | null | undefined): string {
  if (!code) return '—';
  return SOCIAL_REASON_LABEL[code] ?? code;
}

export function socialReasonsText(reasons: string[]): string {
  if (!reasons.length) return '—';
  return reasons.map(socialReasonLabel).join('、');
}

// ── EmptyState（§D8：无假绿、无空白面板）──
export type SocialEmptyKind = 'no_run' | 'paused_exempt' | 'has_run';

export function socialSourceEmptyKind(s: SocialDataSource): SocialEmptyKind {
  if (s.config_status === 'PAUSED' || s.sla_verdict === 'exempt') return 'paused_exempt';
  if (s.latest_run == null || s.sla_reasons.includes('SOCIAL_NO_RUN')) return 'no_run';
  return 'has_run';
}

export const SOCIAL_EMPTY_TEXT: Record<Exclude<SocialEmptyKind, 'has_run'>, string> = {
  no_run: '暂无社交采集记录 / 等待首次 REDFOX 采集',
  paused_exempt: 'REDFOX 社交采集已暂停（exempt，不计 SLA）',
};

export const SOCIAL_RUNS_EMPTY_TEXT = '暂无社交采集 run 记录';

// ── 透明 copy（§D6：用 API disclosure；仅缺失时用保守 fallback）──
export const SOCIAL_DISCLOSURE_FALLBACK =
  'REDFOX 为社交热度/传播信号层，来自选定平台与关键词采样，非全网全量；情绪/风险标签系统派生且未验证，不计入 VOC/NPS 风险分。';

export function socialDisclosure(s: Pick<SocialDataSource, 'disclosure'>): string {
  return s.disclosure && s.disclosure.trim() ? s.disclosure : SOCIAL_DISCLOSURE_FALLBACK;
}

// ── E4 redaction allowlist（前端类型/渲染只用这些 public 字段）──
export const SOCIAL_RUN_PUBLIC_FIELDS = [
  'run_id', 'week', 'run_status', 'platform', 'query', 'api_call_count', 'items_returned',
  'after_dedupe', 'rows_inserted', 'rows_updated', 'rows_skipped', 'budget_max_calls',
  'budget_max_posts', 'host_role', 'raw_field_version', 'crawl_time', 'run_reason',
] as const;

/** 这些 raw 列**绝不**进前端类型/渲染（仅供测试断言不泄漏）。 */
export const SOCIAL_RUN_FORBIDDEN_FIELDS = ['error_message', 'rate_limit_meta'] as const;
